import { Injectable, inject } from '@angular/core';
import { AuthenticationService } from '../../services/authentication.service';
import { FeatureActionsMap } from '../../classes/authentication/user.class';
import { AppointmentActivity, CovisitorAccess } from '@omni/classes/activity/appointment.activity.class';
import { ActivityService } from '../activity/activity.service';

export interface MeetingFeatureFlags {
  coVisitorAccess: CovisitorAccess;
  openOpportunityDynamic: boolean;
  isMeetingTypeFAEnabled: boolean;
  hasMeetingAssetsEnabled: boolean;
  isMeetingObjectiveSelectionEnabled: boolean;
  showPresentationPlayDuration: boolean;
  GPSCheckInEnabled: boolean;
  manualGPSCheckinEnabled: boolean;
  meetingDurationEnabled: boolean;
  meetingEndDateEnabled: boolean;
  meetingDurationRequired: boolean;
  teamsMeetingEnabled: boolean;
  remoteFeatureEnabled: boolean;
  meetingAutoSubjectEnabled: boolean;
  meetingObjectivesEnabled: boolean;
  meetingAttachmentFAEnabled: boolean;
  meetingAttachmentEnabled: boolean;
  meetingNotesEnabled: boolean;
  notesEnabled: boolean;
  therapeuticAreaEnabled: boolean;
  meetingNotesRelabelEnabled: boolean;
  diseaseAreaEnabled: boolean;
  productIndicationEnabled: boolean;
  procedureSectionEnabled: boolean;
  supportingMaterialsEnabled: boolean;
  jointMeetingEnabled: boolean;
  meetingContentReasonEnabled: boolean;
  disableGPSCheckInFlowForHCPMeeting: boolean;
  lockStartEndTimeIfContentPlayed: boolean;
  selfClaimNeededIfStartEndTimeModified: boolean;
  showLessThanResultButtonForContact: boolean;
  showLessThanResultButtonForAccount: boolean;
  opportunitySelectionEnabled: boolean;
  eventsEnabled: boolean;
  gpsFunctionalityEnabled: boolean;
  gpsCheckoutMandatory: boolean;
  cancelMeetingWithReasonEnabled: boolean;
  coachingEnabled: boolean;
  keyMessageSelectionEnabled: boolean;
  statusEnabled: boolean;
  locationEnabled: boolean;
  accountSelectionEnabled: boolean;
  allocationEnabled: boolean;
  sampleDeliveryRequestEnabled: boolean;
  sampleDropEnabled: boolean;
  promoMaterialEnabled: boolean;
  liveMeet: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class MeetingFeatureFlagsService {
  private readonly authService = inject(AuthenticationService);
  private readonly activityService = inject(ActivityService);
  /**
   * Get all meeting feature flags based on user permissions and business settings
   * @param selectedActivity - The currently selected activity
   * @param isAccountVisitRecord - Whether this is an account visit record
   * @param isAccountVisitNestedMeeting - Whether this is a nested meeting
   * @returns MeetingFeatureFlags object with all feature flags
   */
  getMeetingFeatureFlags(activity: AppointmentActivity): MeetingFeatureFlags {
    let hasMeetingObjectives = false;
    let isNotesEnabled = false;
    let isMeetingNotesEnabled = this.authService.hasFeatureAction(FeatureActionsMap.MEETING_NOTES_CAPTURE);
    let isMeetingAttachmentEnabled = this.authService.hasFeatureAction(FeatureActionsMap.MEETING_ATTACHMENT_UPLOAD);
    let isCoachingEnabled = false;
    let isAutoSubjectEnabled = true;
    let isKeyMessageSelectionEnabled = false;
    let isStatusEnabled = false;
    let isLocationEnabled = false;
    let isAccountSelectionEnabled = false;
    let isAllocationEnabled = false;
    let isSampleDeliveryRequestEnabled = false;
    let isPromoMaterialEnabled = false;
    let isTherapeuticAreaEnabled = this.authService.hasFeatureAction(FeatureActionsMap.THERAPEUTIC_AREA);
    if(activity.isLiveMeet) {
      isAutoSubjectEnabled = true;
      isTherapeuticAreaEnabled = true;
    } else {
      isKeyMessageSelectionEnabled = true;
      isLocationEnabled = true;
      isStatusEnabled = true;
      isAccountSelectionEnabled = true;
      isAllocationEnabled = this.authService.hasFeatureAction(FeatureActionsMap.ALLOCATION_TOOL);
      isSampleDeliveryRequestEnabled = this.authService.hasFeatureAction(FeatureActionsMap.SAMPLE_DELIVERY_REQUESTS);
      isPromoMaterialEnabled = this.authService.hasFeatureAction(FeatureActionsMap.PROMOTIONAL_MATERIAL_DROP);
      hasMeetingObjectives = this.authService.hasFeatureAction(FeatureActionsMap.MEETING_OBJECTIVES);
      isNotesEnabled = (isMeetingNotesEnabled || isMeetingAttachmentEnabled) ? false : this.authService.hasFeatureAction(FeatureActionsMap.MEETING_NOTES);
      isCoachingEnabled = this.authService.hasFeatureAction(FeatureActionsMap.COACHING_TOOL) || this.authService.hasFeatureAction(FeatureActionsMap.COACHING_TEAM_VIEW);
    }

    let isMeetingDurationEnabled = this.authService.hasFeatureAction({ featureAction: FeatureActionsMap.MEETING_DURATION, skipLDCheck: true });
    let hasMeetingDurationRequired = this.authService.hasFeatureAction({ featureAction: FeatureActionsMap.MEETING_DURATION_REQUIRED, skipLDCheck: true });
    let isMeetingEndDateEnabled = this.authService.hasFeatureAction(FeatureActionsMap.MEETING_REMOVE_END_DATE) ? false : !isMeetingDurationEnabled;

    return {
      coVisitorAccess: this.authService.user.buSettings && this.authService.user.buSettings['indskr_covisitoraccessonmeeting'],
      openOpportunityDynamic: this.authService.user.buSettings && this.authService.user.buSettings['indskr_openopportunityindynamics'] || false,
      lockStartEndTimeIfContentPlayed: this.authService.user.buSettings && this.authService.user.buSettings['indskr_lockappointmentstartendtimeifcontentplaye'] != null ? this.authService.user.buSettings['indskr_lockappointmentstartendtimeifcontentplaye'] : false,
      selfClaimNeededIfStartEndTimeModified: this.authService.user.buSettings && this.authService.user.buSettings['indskr_selfclaimneededifstartendtimemodified'] != null ? this.authService.user.buSettings['indskr_selfclaimneededifstartendtimemodified'] : false,
      gpsFunctionalityEnabled: this.authService.user.buSettings['indskr_gpsfunctionality'],
      gpsCheckoutMandatory: this.authService.user.buSettings['indskr_gpscheckoutmandatory'],
      isMeetingTypeFAEnabled: this.authService.hasFeatureAction(FeatureActionsMap.MEETING_TYPE),
      hasMeetingAssetsEnabled: this.authService.hasFeatureAction(FeatureActionsMap.TAG_ASSETS_TO_MEETINGS),
      isMeetingObjectiveSelectionEnabled: this.authService.hasFeatureAction(FeatureActionsMap.MEETING_OBJECTIVE_SELECTION),
      showPresentationPlayDuration: this.authService.hasFeatureAction(FeatureActionsMap.ALL_PRESENTATION_PLAY_DURATION),
      GPSCheckInEnabled: this.authService.hasFeatureAction(FeatureActionsMap.ENABLE_GPS_CHECK_IN),
      manualGPSCheckinEnabled: this.authService.hasFeatureAction(FeatureActionsMap.ENABLE_MANUAL_GPS_CHECK_IN),
      meetingDurationEnabled: isMeetingDurationEnabled,
      meetingEndDateEnabled: isMeetingEndDateEnabled,
      meetingDurationRequired: hasMeetingDurationRequired,
      teamsMeetingEnabled: this.authService.hasFeatureAction(FeatureActionsMap.TEAMS_MEETING),
      remoteFeatureEnabled: this.authService.hasFeatureAction(FeatureActionsMap.REMOTE_FEATURE),
      meetingAutoSubjectEnabled: this.authService.hasFeatureAction(FeatureActionsMap.MEETING_AUTO_SUBJECT),
      meetingObjectivesEnabled: hasMeetingObjectives,
      meetingAttachmentEnabled: isMeetingAttachmentEnabled,
      meetingAttachmentFAEnabled: isMeetingAttachmentEnabled ? false : this.authService.hasFeatureAction(FeatureActionsMap.MEETING_ATTACHMENT_NOTES),
      meetingNotesEnabled: isMeetingNotesEnabled,
      notesEnabled: isNotesEnabled,
      therapeuticAreaEnabled: isTherapeuticAreaEnabled,
      meetingNotesRelabelEnabled: this.authService.hasFeatureAction(FeatureActionsMap.MEETING_NOTES_RELABEL),
      diseaseAreaEnabled: this.authService.hasFeatureAction(FeatureActionsMap.MEETING_DISEASE_AREA),
      productIndicationEnabled: this.authService.hasFeatureAction(FeatureActionsMap.ENABLE_INDICATIONS),
      procedureSectionEnabled: this.authService.hasFeatureAction(FeatureActionsMap.MEETING_PROCEDURE),
      supportingMaterialsEnabled: this.authService.hasFeatureAction(FeatureActionsMap.MEETING_SUPPORTING_MATERIALS),
      jointMeetingEnabled: this.authService.hasFeatureAction(FeatureActionsMap.JOINT_MEETING),
      meetingContentReasonEnabled: this.authService.hasFeatureAction(FeatureActionsMap.MEETING_CONTENT_REASON),
      disableGPSCheckInFlowForHCPMeeting: this.authService.hasFeatureAction(FeatureActionsMap.DISABLE_GPS_CHECKIN_FEATURE_IN_HCP_MEETINGS),
      showLessThanResultButtonForContact: this.authService.hasFeatureAction(FeatureActionsMap.CONTACTS_GLOBAL_SEARCH),
      showLessThanResultButtonForAccount: this.authService.hasFeatureAction(FeatureActionsMap.ACCOUNT_GLOBAL_SEARCH),
      opportunitySelectionEnabled: this.authService.hasFeatureAction(FeatureActionsMap.ASSOCIATE_OPPORTUNITIES_TO_MEETINGS),
      eventsEnabled: this.authService.hasFeatureAction(FeatureActionsMap.EVENTS_IN_MEETINGS),
      cancelMeetingWithReasonEnabled: this.authService.user.buSettings && this.authService.user.buSettings['indskr_cancelmeetingwithreason'] != null ? this.authService.user.buSettings['indskr_cancelmeetingwithreason'] : false,
      coachingEnabled: isCoachingEnabled,
      keyMessageSelectionEnabled: isKeyMessageSelectionEnabled,
      statusEnabled: isStatusEnabled,
      locationEnabled: isLocationEnabled,
      accountSelectionEnabled: isAccountSelectionEnabled,
      allocationEnabled: isAllocationEnabled || isPromoMaterialEnabled,
      sampleDeliveryRequestEnabled: isSampleDeliveryRequestEnabled,
      sampleDropEnabled: isAllocationEnabled && this.authService.hasFeatureAction(FeatureActionsMap.ALLOCATION_ORDER_ACTIVITY),
      promoMaterialEnabled: isPromoMaterialEnabled,
      liveMeet: activity.isLiveMeet,
    };
  }

  
}

